import os from "os";
import path from "path";
import fs from "fs";
import { CrunchConfig } from "./types";

export class ConfigManager {
  private configPath = path.join(os.homedir(), ".crunch", "config.json");

  private defaultConfig: CrunchConfig = {
    url: "localhost", // Default to localhost for development
    wallet: "~/.config/solana/id.json",
    loglevel: "info",
    profiles: {},
  };

  load(): CrunchConfig {
    try {
      if (fs.existsSync(this.configPath)) {
        const configData = fs.readFileSync(this.configPath, "utf8");
        const config = JSON.parse(configData);
        return { ...this.defaultConfig, ...config };
      }
    } catch {
      console.warn("Failed to load config, using defaults");
    }

    // Auto-create config file on first run (as documented)
    this.initializeIfNeeded();
    return this.defaultConfig;
  }

  save(config: CrunchConfig): void {
    try {
      const configDir = path.dirname(this.configPath);
      if (!fs.existsSync(configDir)) {
        fs.mkdirSync(configDir, { recursive: true });
      }
      fs.writeFileSync(this.configPath, JSON.stringify(config, null, 2));
    } catch (error) {
      console.error("Failed to save config:", error);
    }
  }

  set(key: string, value: string): void {
    const validKeys = ["url", "wallet", "loglevel", "output", "multisigAddress", "apiKey"];
    if (!validKeys.includes(key)) {
      throw new Error(`Invalid configuration key: ${key}. Valid keys: ${validKeys.join(", ")}`);
    }

    // Validate URL values (accepts URLs and monikers)
    if (key === "url") {
      // Allow clearing the URL by setting it to empty string
      if (
        value &&
        value !== "" &&
        !value.startsWith("http://") &&
        !value.startsWith("https://") &&
        ![
          "m",
          "d",
          "t",
          "l",
          "mainnet",
          "devnet",
          "testnet",
          "localhost",
          "mainnet-beta",
          "localnet",
        ].includes(value.toLowerCase())
      ) {
        throw new Error(
          `Invalid URL: ${value}. Must be a valid HTTP/HTTPS URL or a network moniker (m, d, t, l, mainnet, devnet, testnet, localhost)`
        );
      }
    }

    // Validate loglevel values
    if (key === "loglevel" && !["debug", "info", "warn", "error"].includes(value)) {
      throw new Error(`Invalid loglevel: ${value}. Valid levels: debug, info, warn, error`);
    }

    // Validate format values
    if (key === "output" && !["json"].includes(value)) {
      throw new Error(`Invalid format: ${value}. Valid formats: json`);
    }

    // Validate apiKey values
    if (key === "apiKey" && value && value.trim().length === 0) {
      throw new Error("API key cannot be empty. Use an empty string to remove it.");
    }

    const config = this.load();
    if ((key === "url" || key === "apiKey") && value === "") {
      // Remove the field when setting to empty string
      delete (config as any)[key];
    } else {
      (config as any)[key] = value;
    }
    this.save(config);
  }

  useProfile(profileName: string): void {
    const config = this.load();
    const profile = config.profiles[profileName];
    if (profile) {
      // Apply profile settings to active config, preserving profiles
      const updatedConfig = { ...config };
      Object.assign(updatedConfig, profile);
      // Preserve the original profiles object
      updatedConfig.profiles = config.profiles;
      this.save(updatedConfig);
    } else {
      throw new Error(`Profile '${profileName}' not found`);
    }
  }

  // List available profiles
  listProfiles(): string[] {
    const config = this.load();
    return Object.keys(config.profiles);
  }

  // Initialize config file with defaults if it doesn't exist
  initializeIfNeeded(): void {
    if (!fs.existsSync(this.configPath)) {
      this.save(this.defaultConfig);
    }
  }

  // Save current configuration as a profile
  saveCurrentAsProfile(profileName: string): void {
    if (!profileName || profileName.trim() === "") {
      throw new Error("Profile name cannot be empty");
    }

    const config = this.load();

    // Create profile from current base settings (excluding profiles)
    const profileData: Partial<CrunchConfig> = {};

    if (config.url) profileData.url = config.url;
    if (config.wallet) profileData.wallet = config.wallet;
    if (config.loglevel) profileData.loglevel = config.loglevel;
    if (config.output) profileData.output = config.output;
    if (config.multisigAddress) profileData.multisigAddress = config.multisigAddress;
    if (config.apiKey) profileData.apiKey = config.apiKey;

    // Ensure profiles object exists
    if (!config.profiles) {
      config.profiles = {};
    }

    // Save the profile
    config.profiles[profileName] = profileData;

    this.save(config);
  }
}
